/**
 * DocumentsListeController — page /documents-liste.html (EPIC-007 / 7.7,
 * spec §23 Documents publics).
 *
 * Câble les 3 onglets de la page :
 *   - Mes documents : table CRUD + URL publique + régénération token
 *   - Ajouter un document : formulaire upload (mode "add" ou "edit")
 *   - Pièces jointes par défaut : checkboxes devis/facture
 *
 * Endpoints (cf. api.js EPIC-007/7.4) :
 *   - GET    /organization/documents
 *   - POST   /organization/documents (multipart)
 *   - PUT    /organization/documents/{id}
 *   - DELETE /organization/documents/{id}
 *   - POST   /organization/default-attachments/invoices
 *   - POST   /organization/default-attachments/quotes
 *   - POST   /organization/regenerate-public-token
 *
 * Convention CLAUDE.md respectée :
 *   - 0 HTML inline en JS (templates + cloneTemplate + fillSlots + createElement).
 *   - Aucune classe CSS inventée — toutes ⊆ notamicro.css.
 *   - Vocabulaire UI : « Plateforme Agréée (PA) », pas « PDP », pas NotaFacto.
 */

import BaseController from "./base_controller.js";
import { api, APIError } from "/js/services/api.js";

const CATEGORY_PILL_CLASS = {
  "RC Pro": "nm-pill--olive",
  "Décennale": "nm-pill--ochre",
  "Extrait SIRENE": "nm-pill--blue",
};

const ERROR_MESSAGES = {
  invalid_mime: "Format non supporté. Seuls les fichiers PDF sont acceptés.",
  file_too_large: "Fichier trop volumineux. Taille max : 10 Mo.",
  validation_failed: "Certains champs sont invalides. Vérifiez votre saisie.",
  document_not_found: "Ce document n'existe pas ou a été supprimé.",
  pdf_signature_invalid: "Le fichier ne semble pas être un PDF valide.",
  unauthorized: "Vous n'êtes pas autorisé à effectuer cette action.",
};

const DAYS_PER_MS = 1000 * 60 * 60 * 24;

export default class DocumentsListeController extends BaseController {
  static targets = [
    "documentsTable", "documentsContent", "documentsLoading", "documentsError", "documentsEmpty",
    "documentsCount",
    "urlPublicInput", "urlPublicLink",
    "uploadForm", "uploadName", "uploadCategory", "uploadExpiration",
    "uploadActivity", "uploadActivityField", "uploadPublic",
    "uploadFileInput", "uploadFileLabel",
    "uploadSubmit", "uploadHint", "uploadArchive",
    "ajouterTab", "ajouterPanelHead",
    "editUsageCard", "editUsageIntro",
    "editUsageQuotesSection", "editUsageQuotesList",
    "editUsageInvoicesSection", "editUsageInvoicesList",
    "defaultsQuoteList", "defaultsInvoiceList",
    "defaultsQuoteCounter", "defaultsInvoiceCounter",
    "defaultsSubmit",
  ];

  // État interne
  _documents = [];
  _defaultAttachments = { quoteIds: [], invoiceIds: [] };
  _activities = [];
  _publicToken = null;
  _publicSlug = null;
  _pendingFile = null;
  _editingId = null;

  async connect() {
    await this.refresh();
  }

  // ---------------------------------------------------------------------------
  // Initial load
  // ---------------------------------------------------------------------------

  async refresh() {
    this._showLoading();
    try {
      const [docsResp, activitiesResp] = await Promise.all([
        api.listDocuments(),
        this._safeListActivities(),
      ]);

      this._documents = Array.isArray(docsResp?.documents) ? docsResp.documents : [];
      this._defaultAttachments = {
        quoteIds: Array.isArray(docsResp?.defaultAttachments?.quotes)
          ? docsResp.defaultAttachments.quotes.slice()
          : [],
        invoiceIds: Array.isArray(docsResp?.defaultAttachments?.invoices)
          ? docsResp.defaultAttachments.invoices.slice()
          : [],
      };
      this._publicToken = docsResp?.publicToken ?? null;
      this._publicSlug = docsResp?.publicSlug ?? "dossier-public";
      this._activities = activitiesResp;

      this._renderDocuments();
      this._renderPublicUrl();
      this._renderActivitySelect();
      this._renderDefaultsLists();
    } catch (err) {
      this._showDocumentsError(this._extractMessage(err) || "Impossible de charger vos documents.");
    }
  }

  async _safeListActivities() {
    try {
      const resp = await api.listActivities();
      return Array.isArray(resp?.activities) ? resp.activities : [];
    } catch {
      return [];
    }
  }

  // ---------------------------------------------------------------------------
  // Render — documents table
  // ---------------------------------------------------------------------------

  _renderDocuments() {
    const tbody = this.documentsTableTarget;
    tbody.textContent = "";

    if (this._documents.length === 0) {
      this._showDocumentsEmpty();
      return;
    }

    const sorted = this._documents.slice().sort((a, b) => {
      const sa = a.status === "archived" ? 1 : 0;
      const sb = b.status === "archived" ? 1 : 0;
      if (sa !== sb) return sa - sb;
      return (a.name || "").localeCompare(b.name || "", "fr");
    });

    sorted.forEach((doc) => tbody.appendChild(this._buildDocumentRow(doc)));

    this._showDocumentsContent();
    this._renderDocumentsCount();
  }

  _renderDocumentsCount() {
    if (!this.hasDocumentsCountTarget) return;
    const total = this._documents.length;
    const publics = this._documents.filter((d) => d.isPublic && d.status !== "archived").length;
    const privates = this._documents.filter((d) => !d.isPublic && d.status !== "archived").length;
    this.documentsCountTarget.textContent =
      `${total} document${total > 1 ? "s" : ""} · ${publics} public${publics > 1 ? "s" : ""} · ${privates} privé${privates > 1 ? "s" : ""}`;
  }

  _buildDocumentRow(doc) {
    const fragment = this.cloneTemplate("tpl-document-row");
    const tr = fragment.querySelector("tr");
    if (!tr) return fragment;

    tr.dataset.id = doc.id;
    if (doc.status === "archived") tr.dataset.archived = "true";

    // Nom + meta (filename + taille)
    this.fillSlots(tr, {
      name: doc.name || "—",
      meta: this._formatDocMeta(doc),
      category: doc.category || "—",
    });

    // Si archivé : barré + texte « archivée le … »
    const nameEl = tr.querySelector('[data-slot="name"]');
    if (nameEl && doc.status === "archived") {
      nameEl.classList.add("text-subtle");
      nameEl.style.textDecoration = "line-through";
    }

    // Échéance (date + pill « expire dans X j » si proche)
    const expEl = tr.querySelector('[data-slot="expiration"]');
    if (expEl) {
      expEl.textContent = "";
      this._fillExpirationCell(expEl, doc);
    }

    // Public / privé / —
    const pubEl = tr.querySelector('[data-slot="public"]');
    if (pubEl) this._fillPublicCell(pubEl, doc);

    // Statut (actif / archivé)
    const statusEl = tr.querySelector('[data-slot="status"]');
    if (statusEl) this._fillStatusCell(statusEl, doc);

    // Bouton action
    const actionBtn = tr.querySelector('[data-slot="action"]');
    if (actionBtn) {
      actionBtn.dataset.id = doc.id;
      if (doc.status === "archived") {
        actionBtn.textContent = "Restaurer";
        actionBtn.setAttribute("aria-label", `Restaurer le document ${doc.name || ""}`);
        // V1 — restauration non implémentée backend. Désactivé.
        actionBtn.disabled = true;
        actionBtn.title = "La restauration sera disponible ultérieurement.";
      } else {
        actionBtn.textContent = "Modifier";
        actionBtn.setAttribute("aria-label", `Modifier le document ${doc.name || ""}`);
      }
    }

    return fragment;
  }

  _formatDocMeta(doc) {
    const parts = [];
    if (doc.fileName) parts.push(doc.fileName);
    if (doc.fileSize) parts.push(this._formatBytes(doc.fileSize));
    if (doc.status === "archived" && doc.archivedAt) {
      return `archivé le ${this._formatDate(doc.archivedAt)} · ${this._formatBytes(doc.fileSize || 0)}`;
    }
    return parts.join(" · ");
  }

  _fillExpirationCell(td, doc) {
    if (!doc.expirationDate) {
      td.textContent = "—";
      return;
    }
    const time = document.createElement("time");
    time.setAttribute("datetime", doc.expirationDate);
    time.textContent = this._formatDate(doc.expirationDate);
    td.appendChild(time);

    if (doc.status === "archived") return;

    const daysLeft = Math.round((new Date(doc.expirationDate).getTime() - Date.now()) / DAYS_PER_MS);
    if (daysLeft <= 120 && daysLeft >= 0) {
      const pill = document.createElement("span");
      pill.className = "nm-pill nm-pill--ochre";
      pill.style.marginLeft = "6px";
      const dot = document.createElement("span");
      dot.className = "nm-pill__dot";
      dot.setAttribute("aria-hidden", "true");
      pill.appendChild(dot);
      pill.appendChild(document.createTextNode(`expire dans ${daysLeft} j`));
      td.appendChild(pill);
    } else if (daysLeft < 0) {
      const pill = document.createElement("span");
      pill.className = "nm-pill nm-pill--danger";
      pill.style.marginLeft = "6px";
      const dot = document.createElement("span");
      dot.className = "nm-pill__dot";
      dot.setAttribute("aria-hidden", "true");
      pill.appendChild(dot);
      pill.appendChild(document.createTextNode("expirée"));
      td.appendChild(pill);
    }
  }

  _fillPublicCell(td, doc) {
    if (doc.status === "archived") {
      td.classList.add("is-muted");
      td.textContent = "—";
      return;
    }
    const pill = document.createElement("span");
    pill.className = doc.isPublic ? "nm-pill nm-pill--olive" : "nm-pill";
    const dot = document.createElement("span");
    dot.className = "nm-pill__dot";
    dot.setAttribute("aria-hidden", "true");
    pill.appendChild(dot);
    pill.appendChild(document.createTextNode(doc.isPublic ? "oui" : "privé"));
    td.appendChild(pill);
  }

  _fillStatusCell(td, doc) {
    const pill = document.createElement("span");
    pill.className = doc.status === "archived" ? "nm-pill" : "nm-pill nm-pill--blue";
    const dot = document.createElement("span");
    dot.className = "nm-pill__dot";
    dot.setAttribute("aria-hidden", "true");
    pill.appendChild(dot);
    pill.appendChild(document.createTextNode(doc.status === "archived" ? "archivé" : "actif"));
    td.appendChild(pill);
  }

  // ---------------------------------------------------------------------------
  // Render — URL publique
  // ---------------------------------------------------------------------------

  _renderPublicUrl() {
    if (!this.hasUrlPublicInputTarget) return;
    if (!this._publicToken) {
      this.urlPublicInputTarget.value = "URL non disponible";
      if (this.hasUrlPublicLinkTarget) {
        this.urlPublicLinkTarget.setAttribute("href", "#");
        this.urlPublicLinkTarget.setAttribute("aria-disabled", "true");
      }
      return;
    }
    const path = `/p/${encodeURIComponent(this._publicToken)}/${encodeURIComponent(this._publicSlug)}`;
    const fullUrl = `${window.location.host}${path}`;
    this.urlPublicInputTarget.value = fullUrl;
    if (this.hasUrlPublicLinkTarget) {
      this.urlPublicLinkTarget.setAttribute("href", path);
      this.urlPublicLinkTarget.removeAttribute("aria-disabled");
    }
  }

  // ---------------------------------------------------------------------------
  // Render — activity select (regulated only)
  // ---------------------------------------------------------------------------

  _renderActivitySelect() {
    if (!this.hasUploadActivityTarget) return;
    const regulated = this._activities.filter(
      (a) => a.regulatedCategory && a.status !== "archived",
    );

    // Reset puis re-population.
    this.uploadActivityTarget.textContent = "";
    const noneOption = document.createElement("option");
    noneOption.value = "";
    noneOption.textContent = "— Aucune —";
    this.uploadActivityTarget.appendChild(noneOption);

    regulated.forEach((a) => {
      const opt = document.createElement("option");
      opt.value = a.id;
      const cat = this._categoryLabel(a.category);
      opt.textContent = `${a.description || "Activité"} (${cat})`;
      this.uploadActivityTarget.appendChild(opt);
    });

    if (this.hasUploadActivityFieldTarget) {
      this.uploadActivityFieldTarget.hidden = regulated.length === 0;
    }
  }

  _categoryLabel(cat) {
    if (cat === "bic_vente") return "BIC vente";
    if (cat === "bic_services") return "BIC services";
    if (cat === "bnc") return "BNC";
    return cat || "—";
  }

  // ---------------------------------------------------------------------------
  // Render — defaults lists (checkboxes)
  // ---------------------------------------------------------------------------

  _renderDefaultsLists() {
    const eligibles = this._documents.filter((d) => d.status !== "archived");
    this._renderDefaultsList(this.defaultsQuoteListTarget, eligibles, this._defaultAttachments.quoteIds, "quote");
    this._renderDefaultsList(this.defaultsInvoiceListTarget, eligibles, this._defaultAttachments.invoiceIds, "invoice");
    this._updateDefaultsCounters();
  }

  _renderDefaultsList(ul, docs, selectedIds, scope) {
    if (!ul) return;
    ul.textContent = "";

    if (docs.length === 0) {
      const li = document.createElement("li");
      li.className = "fs-13 text-muted";
      li.style.padding = "10px 0";
      li.textContent = "Aucun document éligible. Téléversez un PDF dans l'onglet « Ajouter ».";
      ul.appendChild(li);
      return;
    }

    docs.forEach((doc, idx) => {
      const fragment = this.cloneTemplate("tpl-attachment-checkbox");
      const li = fragment.querySelector("li");
      if (!li) return;

      // Bordure : toujours top, last-child reçoit aussi la bottom.
      if (idx === docs.length - 1) {
        li.style.borderBottom = "1px solid var(--border)";
      }

      const inputEl = fragment.querySelector('[data-slot="input"]');
      const labelEl = fragment.querySelector('[data-slot="label"]');
      const nameEl = fragment.querySelector('[data-slot="name"]');
      const metaEl = fragment.querySelector('[data-slot="meta"]');

      const inputId = `def-${scope}-${doc.id}`;
      if (inputEl) {
        inputEl.id = inputId;
        inputEl.value = doc.id;
        inputEl.dataset.scope = scope;
        if (selectedIds.includes(doc.id)) inputEl.checked = true;
      }
      if (labelEl) labelEl.setAttribute("for", inputId);
      if (nameEl) nameEl.textContent = doc.name || "—";
      if (metaEl) {
        metaEl.textContent = "";
        metaEl.appendChild(document.createTextNode(doc.category || "—"));
        if (!doc.isPublic) {
          metaEl.appendChild(document.createTextNode(" · "));
          const pill = document.createElement("span");
          pill.className = "nm-pill";
          pill.style.marginLeft = "4px";
          const dot = document.createElement("span");
          dot.className = "nm-pill__dot";
          dot.setAttribute("aria-hidden", "true");
          pill.appendChild(dot);
          pill.appendChild(document.createTextNode("privé"));
          metaEl.appendChild(pill);
        } else if (doc.expirationDate) {
          metaEl.appendChild(document.createTextNode(` · échéance ${this._formatDate(doc.expirationDate)}`));
        }
      }

      ul.appendChild(fragment);
    });
  }

  _updateDefaultsCounters() {
    if (this.hasDefaultsQuoteCounterTarget) {
      const n = this._countCheckedInList(this.defaultsQuoteListTarget);
      this.defaultsQuoteCounterTarget.textContent = `${n} document${n > 1 ? "s" : ""} sera joint à chaque devis.`;
    }
    if (this.hasDefaultsInvoiceCounterTarget) {
      const n = this._countCheckedInList(this.defaultsInvoiceListTarget);
      this.defaultsInvoiceCounterTarget.textContent = `${n} document${n > 1 ? "s" : ""} sera joint à chaque facture.`;
    }
  }

  _countCheckedInList(ul) {
    if (!ul) return 0;
    return Array.from(ul.querySelectorAll('input[type="checkbox"]')).filter((i) => i.checked).length;
  }

  updateDefaultsCounter() {
    this._updateDefaultsCounters();
  }

  // ---------------------------------------------------------------------------
  // Action — copier l'URL publique
  // ---------------------------------------------------------------------------

  async copyPublicUrl() {
    if (!this.hasUrlPublicInputTarget || !this.urlPublicInputTarget.value) return;
    const value = this.urlPublicInputTarget.value;
    try {
      await navigator.clipboard.writeText(value);
      this.showSuccess("Lien copié dans le presse-papiers.");
    } catch {
      this.urlPublicInputTarget.select();
      this.showError("Impossible de copier automatiquement. Utilisez Ctrl+C / Cmd+C.");
    }
  }

  // ---------------------------------------------------------------------------
  // Action — régénérer le token public
  // ---------------------------------------------------------------------------

  async regenerateToken() {
    const ok = await this.confirm(
      "Régénérer l'URL publique ?\n\n"
      + "Les anciens liens partagés (sur PDF déjà émis, en signature email, sur les réseaux) "
      + "cesseront de fonctionner. Cette action est irréversible.",
      { title: "Régénérer l'URL publique", confirmLabel: "Régénérer", cancelLabel: "Annuler" },
    );
    if (!ok) return;

    try {
      const resp = await api.regeneratePublicToken();
      if (resp?.publicToken) this._publicToken = resp.publicToken;
      this._renderPublicUrl();
      this.showSuccess("URL publique régénérée.");
    } catch (err) {
      this.showError(this._extractMessage(err) || "Impossible de régénérer l'URL.");
    }
  }

  // ---------------------------------------------------------------------------
  // Action — édition (passe au tab Ajouter en mode édition)
  // ---------------------------------------------------------------------------

  editDocument(event) {
    const id = event.currentTarget?.dataset?.id;
    if (!id) return;
    const doc = this._documents.find((d) => d.id === id);
    if (!doc) return;

    this._enterEditMode(doc);
    window.location.hash = "ajouter";
  }

  _enterEditMode(doc) {
    this._editingId = doc.id;
    this._pendingFile = null;

    if (this.hasUploadNameTarget) this.uploadNameTarget.value = doc.name || "";
    if (this.hasUploadCategoryTarget) this.uploadCategoryTarget.value = doc.category || "";
    if (this.hasUploadExpirationTarget) this.uploadExpirationTarget.value = doc.expirationDate || "";
    if (this.hasUploadPublicTarget) this.uploadPublicTarget.checked = !!doc.isPublic;
    if (this.hasUploadActivityTarget) {
      this.uploadActivityTarget.value = doc.linkedInsuranceActivityId || "";
    }
    if (this.hasUploadFileInputTarget) {
      this.uploadFileInputTarget.value = "";
      this.uploadFileInputTarget.disabled = true;
    }
    if (this.hasUploadFileLabelTarget) {
      this.uploadFileLabelTarget.textContent = "Le fichier PDF n'est pas modifiable. Pour le remplacer, archivez ce document et téléversez-en un nouveau.";
    }
    if (this.hasUploadSubmitTarget) {
      this.uploadSubmitTarget.textContent = "Enregistrer les modifications";
    }
    if (this.hasUploadHintTarget) {
      this.uploadHintTarget.textContent = "— Mode édition";
    }
    if (this.hasUploadArchiveTarget) {
      this.uploadArchiveTarget.hidden = false;
    }

    // C-22 — relabel onglet + en-tête en mode édition.
    if (this.hasAjouterTabTarget) this.ajouterTabTarget.textContent = "Modifier un document";
    if (this.hasAjouterPanelHeadTarget) this.ajouterPanelHeadTarget.textContent = "— Modifier un document";

    // C-21 — liste read-only des devis/factures où ce document est attaché.
    this._renderEditUsage(doc.id);
  }

  // C-21 — rend la liste read-only « Utilisé sur ces devis et factures »
  // dans la sidebar du panneau d'édition. Échec d'API → carte masquée.
  async _renderEditUsage(docId) {
    if (!this.hasEditUsageCardTarget) return;

    let attachments;
    try {
      attachments = await api.getDocumentAttachments(docId);
    } catch {
      this.editUsageCardTarget.hidden = true;
      return;
    }
    // L'utilisateur peut avoir quitté ou changé de document entre-temps.
    if (this._editingId !== docId) return;

    const quotes = Array.isArray(attachments?.quotes) ? attachments.quotes : [];
    const invoices = Array.isArray(attachments?.invoices) ? attachments.invoices : [];
    const total = quotes.length + invoices.length;

    if (this.hasEditUsageIntroTarget) {
      this.editUsageIntroTarget.textContent = total > 0
        ? `Ce document est attaché à ${total} document${total > 1 ? "s" : ""} émis. L'archivage ne les modifie pas.`
        : "Ce document n'est attaché à aucun devis ni facture pour l'instant.";
    }

    this._fillUsageList(
      this.hasEditUsageQuotesSectionTarget ? this.editUsageQuotesSectionTarget : null,
      this.hasEditUsageQuotesListTarget ? this.editUsageQuotesListTarget : null,
      quotes,
      "quote",
    );
    this._fillUsageList(
      this.hasEditUsageInvoicesSectionTarget ? this.editUsageInvoicesSectionTarget : null,
      this.hasEditUsageInvoicesListTarget ? this.editUsageInvoicesListTarget : null,
      invoices,
      "invoice",
    );

    this.editUsageCardTarget.hidden = false;
  }

  _fillUsageList(section, list, entities, kind) {
    if (!section || !list) return;
    list.textContent = "";
    if (entities.length === 0) {
      section.hidden = true;
      return;
    }
    entities.forEach((e) => list.appendChild(this._buildAttachmentItem(e, kind)));
    section.hidden = false;
  }

  _exitEditMode() {
    this._editingId = null;
    this._pendingFile = null;

    if (this.hasUploadFormTarget) this.uploadFormTarget.reset();
    if (this.hasUploadPublicTarget) this.uploadPublicTarget.checked = true;
    if (this.hasUploadFileInputTarget) {
      this.uploadFileInputTarget.disabled = false;
    }
    if (this.hasUploadFileLabelTarget) {
      this.uploadFileLabelTarget.textContent = "Glissez-déposez votre PDF ici ou";
    }
    if (this.hasUploadSubmitTarget) {
      this.uploadSubmitTarget.textContent = "Téléverser et enregistrer";
    }
    if (this.hasUploadHintTarget) {
      this.uploadHintTarget.textContent = "— Le PDF est téléversé puis scanné";
    }
    if (this.hasUploadArchiveTarget) {
      this.uploadArchiveTarget.hidden = true;
    }

    // C-22 — revert des libellés onglet + en-tête.
    if (this.hasAjouterTabTarget) this.ajouterTabTarget.textContent = "Ajouter un document";
    if (this.hasAjouterPanelHeadTarget) this.ajouterPanelHeadTarget.textContent = "— Téléversement d'un nouveau PDF";

    // C-21 — masque la carte d'usage read-only.
    if (this.hasEditUsageCardTarget) this.editUsageCardTarget.hidden = true;
  }

  // C-22 — un changement d'onglet vers « Mes documents » ou « Pièces
  // jointes » sort du mode édition (revert des libellés relabelés).
  exitEditFromTab() {
    if (this._editingId) this._exitEditMode();
  }

  cancelUpload() {
    this._exitEditMode();
    window.location.hash = "mes-documents";
  }

  // ---------------------------------------------------------------------------
  // Action — submit upload (mode add ou edit)
  // ---------------------------------------------------------------------------

  async submitUpload(event) {
    event.preventDefault();

    const name = this._trimVal(this.uploadNameTarget?.value);
    const category = this._trimVal(this.uploadCategoryTarget?.value);
    const expirationDate = this._trimVal(this.uploadExpirationTarget?.value) || undefined;
    const linkedInsuranceActivityId = this._trimVal(this.uploadActivityTarget?.value) || undefined;
    const isPublic = !!this.uploadPublicTarget?.checked;

    if (!name) {
      this.showError("Le nom du document est obligatoire.");
      return;
    }
    if (!category) {
      this.showError("La catégorie est obligatoire.");
      return;
    }

    const metadata = { name, category, isPublic };
    if (expirationDate) metadata.expirationDate = expirationDate;
    if (linkedInsuranceActivityId) metadata.linkedInsuranceActivityId = linkedInsuranceActivityId;

    if (this.hasUploadSubmitTarget) this.uploadSubmitTarget.disabled = true;

    try {
      if (this._editingId) {
        await api.updateDocument(this._editingId, metadata);
        this.showSuccess("Document mis à jour.");
      } else {
        if (!this._pendingFile) {
          this.showError("Sélectionnez d'abord un fichier PDF.");
          return;
        }
        await api.uploadDocument(this._pendingFile, metadata);
        this.showSuccess("Document téléversé.");
      }
      await this.refresh();
      this._exitEditMode();
      window.location.hash = "mes-documents";
    } catch (err) {
      this.showError(this._extractMessage(err) || "Échec de l'enregistrement.");
    } finally {
      if (this.hasUploadSubmitTarget) this.uploadSubmitTarget.disabled = false;
    }
  }

  // ---------------------------------------------------------------------------
  // Action — archiver depuis le mode édition
  // ---------------------------------------------------------------------------

  async archiveCurrent() {
    if (!this._editingId) return;
    const doc = this._documents.find((d) => d.id === this._editingId);
    const name = doc?.name || "ce document";

    // EPIC-034 / 34.4 — confirmation enrichie : compte les devis et
    // factures attachés pour informer l'utilisateur avant l'archivage.
    // Échec d'API → confirmation habituelle (silencieux).
    let attachedCount = 0;
    try {
      const attachments = await api.getDocumentAttachments(this._editingId);
      attachedCount = (attachments?.quotes?.length ?? 0)
        + (attachments?.invoices?.length ?? 0);
    } catch {
      attachedCount = 0;
    }

    const intro = attachedCount > 0
      ? `Archiver « ${name} » ?\n\nCe document est attaché à ${attachedCount} `
        + `devis/facture${attachedCount > 1 ? "s" : ""}. `
        + "Son archivage n'impactera pas les documents déjà émis mais le "
        + "retirera du dossier public et des pièces jointes par défaut."
      : `Archiver « ${name} » ?\n\n`
        + "Le document sera retiré de votre dossier public et des pièces jointes par défaut. "
        + "Les devis et factures déjà émis ne sont pas modifiés.";

    const ok = await this.confirm(intro, {
      title: "Archiver le document",
      confirmLabel: "Archiver",
      cancelLabel: "Annuler",
    });
    if (!ok) return;

    try {
      await api.deleteDocument(this._editingId);
      this.showSuccess("Document archivé.");
      this._exitEditMode();
      await this.refresh();
      window.location.hash = "mes-documents";
    } catch (err) {
      this.showError(this._extractMessage(err) || "Impossible d'archiver le document.");
    }
  }

  // ---------------------------------------------------------------------------
  // Liste d'usage read-only (devis/factures attachés) — rendue dans le
  // panneau de modification (cf. _renderEditUsage).
  // ---------------------------------------------------------------------------

  _buildAttachmentItem(entity, kind) {
    const templateId = kind === "quote"
      ? "tpl-attachment-quote-item"
      : "tpl-attachment-invoice-item";
    const fragment = this.cloneTemplate(templateId);
    const li = fragment.querySelector("li");
    if (!li) return fragment;

    const link = li.querySelector('[data-slot="link"]');
    if (link instanceof HTMLAnchorElement) {
      const base = kind === "quote" ? "/devis-detail.html" : "/facture-detail.html";
      link.href = `${base}?id=${encodeURIComponent(entity.id)}`;
    }

    const dateIso = kind === "quote" ? entity.dateCreated : entity.dateEmission;
    const dateEl = li.querySelector('[data-slot="date"]');
    if (dateEl instanceof HTMLTimeElement && dateIso) {
      dateEl.dateTime = dateIso;
      dateEl.textContent = this._formatDate(dateIso);
    } else if (dateEl) {
      dateEl.textContent = "—";
    }

    this.fillSlots(li, {
      number: entity.number || "—",
      "client-name": entity.clientName || "—",
      status: entity.status || "—",
    });

    return fragment;
  }

  // ---------------------------------------------------------------------------
  // File-upload events (dispatched by FileUploadController)
  // ---------------------------------------------------------------------------

  handleFileSelected(event) {
    const file = event.detail?.file;
    if (!file) return;
    this._pendingFile = file;

    if (this.hasUploadFileLabelTarget) {
      this.uploadFileLabelTarget.textContent = `Fichier sélectionné : ${file.name} (${this._formatBytes(file.size)})`;
    }

    // Pré-remplit le nom avec le nom du fichier (sans extension) si vide.
    if (this.hasUploadNameTarget && !this.uploadNameTarget.value) {
      const base = file.name.replace(/\.pdf$/i, "");
      this.uploadNameTarget.value = base;
    }
  }

  handleFileError(event) {
    const message = event.detail?.message || "Fichier invalide.";
    this.showError(message);
  }

  // ---------------------------------------------------------------------------
  // Action — submit defaults (devis + facture)
  // ---------------------------------------------------------------------------

  async submitDefaults() {
    const quoteIds = this._collectCheckedIds(this.defaultsQuoteListTarget);
    const invoiceIds = this._collectCheckedIds(this.defaultsInvoiceListTarget);

    if (this.hasDefaultsSubmitTarget) this.defaultsSubmitTarget.disabled = true;

    try {
      await Promise.all([
        api.setDefaultQuoteAttachments(quoteIds),
        api.setDefaultInvoiceAttachments(invoiceIds),
      ]);
      this._defaultAttachments = { quoteIds, invoiceIds };
      this.showSuccess("Sélections enregistrées.");
    } catch (err) {
      this.showError(this._extractMessage(err) || "Échec de l'enregistrement.");
    } finally {
      if (this.hasDefaultsSubmitTarget) this.defaultsSubmitTarget.disabled = false;
    }
  }

  cancelDefaults() {
    this._renderDefaultsLists();
  }

  _collectCheckedIds(ul) {
    if (!ul) return [];
    return Array.from(ul.querySelectorAll('input[type="checkbox"]:checked')).map((i) => i.value);
  }

  // ---------------------------------------------------------------------------
  // States
  // ---------------------------------------------------------------------------

  _showLoading() {
    if (this.hasDocumentsLoadingTarget) this.documentsLoadingTarget.hidden = false;
    if (this.hasDocumentsErrorTarget) this.documentsErrorTarget.hidden = true;
    if (this.hasDocumentsContentTarget) this.documentsContentTarget.hidden = true;
    if (this.hasDocumentsEmptyTarget) this.documentsEmptyTarget.hidden = true;
  }

  _showDocumentsContent() {
    if (this.hasDocumentsLoadingTarget) this.documentsLoadingTarget.hidden = true;
    if (this.hasDocumentsErrorTarget) this.documentsErrorTarget.hidden = true;
    if (this.hasDocumentsContentTarget) this.documentsContentTarget.hidden = false;
    if (this.hasDocumentsEmptyTarget) this.documentsEmptyTarget.hidden = true;
  }

  _showDocumentsEmpty() {
    if (this.hasDocumentsLoadingTarget) this.documentsLoadingTarget.hidden = true;
    if (this.hasDocumentsErrorTarget) this.documentsErrorTarget.hidden = true;
    if (this.hasDocumentsContentTarget) this.documentsContentTarget.hidden = true;
    if (this.hasDocumentsEmptyTarget) this.documentsEmptyTarget.hidden = false;
    if (this.hasDocumentsCountTarget) this.documentsCountTarget.textContent = "0 document";
  }

  _showDocumentsError(message) {
    if (this.hasDocumentsLoadingTarget) this.documentsLoadingTarget.hidden = true;
    if (this.hasDocumentsContentTarget) this.documentsContentTarget.hidden = true;
    if (this.hasDocumentsEmptyTarget) this.documentsEmptyTarget.hidden = true;
    if (this.hasDocumentsErrorTarget) {
      this.documentsErrorTarget.textContent = message;
      this.documentsErrorTarget.hidden = false;
    }
  }

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------

  _trimVal(v) {
    return typeof v === "string" ? v.trim() : "";
  }

  _formatBytes(bytes) {
    if (!bytes || bytes <= 0) return "—";
    if (bytes < 1024) return `${bytes} o`;
    if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} Ko`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
  }

  _formatDate(iso) {
    if (!iso) return "—";
    try {
      const d = new Date(iso);
      return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" });
    } catch {
      return iso;
    }
  }

  _extractMessage(err) {
    if (err instanceof APIError) {
      const code = err.code || err.body?.error?.code;
      if (code && ERROR_MESSAGES[code]) return ERROR_MESSAGES[code];
      return err.body?.error?.message || err.message;
    }
    return err?.message;
  }
}
